import { Notification, NotificationPreferences } from '../types/notification';

const API_BASE_URL =
  process.env.REACT_APP_SERVER_URL ||
  (typeof window !== 'undefined' &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1'
    ? ''
    : 'http://localhost:3001');

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export async function getNotifications(userId: string): Promise<Notification[]> {
  const data = await request<{ notifications: Notification[] }>(
    `/api/notifications?userId=${encodeURIComponent(userId)}`
  );
  return data.notifications.map((n) => ({
    ...n,
    createdAt: new Date(n.createdAt),
  }));
}

export async function markNotificationAsRead(notificationId: string): Promise<void> {
  await request<{ success: boolean }>(`/api/notifications/${notificationId}/read`, {
    method: 'PATCH',
  });
}

export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  await request<{ success: boolean }>('/api/notifications/read-all', {
    method: 'PATCH',
    body: JSON.stringify({ userId }),
  });
}

export async function createNotification(
  notification: Omit<Notification, 'id' | 'createdAt' | 'read'>
): Promise<Notification> {
  const data = await request<{ notification: Notification }>('/api/notifications', {
    method: 'POST',
    body: JSON.stringify(notification),
  });
  return { ...data.notification, createdAt: new Date(data.notification.createdAt) };
}

export async function getNotificationPreferences(userId: string): Promise<NotificationPreferences> {
  const data = await request<{ preferences: NotificationPreferences }>(
    `/api/notifications/preferences?userId=${encodeURIComponent(userId)}`
  );
  return data.preferences;
}

export async function updateNotificationPreferences(
  userId: string,
  preferences: NotificationPreferences
): Promise<NotificationPreferences> {
  const data = await request<{ preferences: NotificationPreferences }>(
    '/api/notifications/preferences',
    {
      method: 'PUT',
      body: JSON.stringify({ userId, preferences }),
    }
  );
  return data.preferences;
}

function getCurrentSeason(): { name: string; message: string; emoji: string } {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) {
    return { name: 'Spring', message: 'Spring is here! Refresh your business card with a new look for the season.', emoji: '🌸' };
  } else if (month >= 5 && month <= 7) {
    return { name: 'Summer', message: 'Summer networking season! Make sure your card is up to date.', emoji: '☀️' };
  } else if (month >= 8 && month <= 10) {
    return { name: 'Fall', message: 'Fall conference season is here! Update your card before your next event.', emoji: '🍂' };
  } else {
    return { name: 'Holiday', message: 'Update your card for the holidays! End the year with fresh connections.', emoji: '❄️' };
  }
}

export async function sendReEngagementNotification(userId: string): Promise<Notification> {
  return createNotification({
    userId,
    type: 're_engagement',
    title: 'We miss you!',
    message: 'We miss you! Update your business card and reconnect with your network.',
    actionUrl: '/card-creation',
    actionLabel: 'Update Your Card',
  });
}

export async function sendSeasonalReminder(userId: string): Promise<Notification> {
  const season = getCurrentSeason();
  return createNotification({
    userId,
    type: 'seasonal_reminder',
    title: `${season.emoji} ${season.name} Card Refresh`,
    message: season.message,
    actionUrl: '/card-creation',
    actionLabel: 'Refresh Your Card',
  });
}

export async function sendWelcomeNotification(userId: string): Promise<Notification> {
  return createNotification({
    userId,
    type: 'system',
    title: 'Welcome to O Business Card!',
    message: 'Welcome to O Business Card! Create your first digital business card and start networking.',
    actionUrl: '/card-creation',
    actionLabel: 'Create Your Card',
  });
}
